import { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity as ActivityIcon,
  AlertTriangle,
  CheckCircle2,
  MessageCircle,
  RefreshCw,
  Rocket,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBrandContext } from "@/contexts/brand-context";
import { useActivity, type ActivityType } from "@/hooks/use-activity";
import { ActivityTimelineItem } from "@/components/activity/activity-item";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocale, formatDate } from "@/i18n/format";
import { groupActivity, type ActivityDay } from "@/lib/activity-feed";
import { daysFromToday } from "@/lib/upcoming-posts";
import { cn } from "@/lib/utils";

const TYPE_FILTERS: Array<{ value: ActivityType; labelKey: string; icon: LucideIcon }> = [
  { value: "post", labelKey: "filter.posts", icon: CheckCircle2 },
  { value: "job", labelKey: "filter.jobs", icon: Sparkles },
  { value: "boost", labelKey: "filter.boosts", icon: Rocket },
  { value: "reply", labelKey: "filter.replies", icon: MessageCircle },
  { value: "review", labelKey: "filter.reviews", icon: ShieldCheck },
];

export function BrandActivityPage() {
  const { t } = useTranslation("activity");
  const locale = useLocale();
  const { brand } = useBrandContext();
  const [selected, setSelected] = useState<ActivityType[]>([]);
  const [problemsOnly, setProblemsOnly] = useState(false);

  // Empty selection = all five types (no filter param sent).
  const query = useActivity(brand.id, selected.length > 0 ? selected : undefined);
  const events = useMemo(() => query.data?.pages.flatMap((p) => p.events) ?? [], [query.data]);
  const allDays = useMemo(() => groupActivity(events), [events]);
  const problemCount = allDays.reduce((n, d) => n + d.items.filter((i) => i.tone === "failed").length, 0);
  const days: ActivityDay[] = problemsOnly
    ? allDays
        .map((d) => ({ ...d, items: d.items.filter((i) => i.tone === "failed") }))
        .filter((d) => d.items.length > 0)
    : allDays;

  const filtered = selected.length > 0 || problemsOnly;
  const clearFilters = () => {
    setSelected([]);
    setProblemsOnly(false);
  };
  const toggleType = (type: ActivityType) =>
    setSelected((cur) => (cur.includes(type) ? cur.filter((x) => x !== type) : [...cur, type]));

  // Load the next page when the end of the list scrolls into view.
  const sentinel = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasNextPage || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
      },
      { rootMargin: "400px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const dayLabel = (date: Date): string => {
    const diff = daysFromToday(date);
    if (diff === 0) return t("day.today");
    if (diff === -1) return t("day.yesterday");
    return formatDate(date, locale, { weekday: "long", month: "long", day: "numeric" });
  };

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-4 py-6 sm:px-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("title")}</h1>
          <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
          aria-label={t("refresh")}
        >
          <RefreshCw className={cn("h-4 w-4 sm:mr-1.5", query.isFetching && "animate-spin")} aria-hidden="true" />
          <span className="hidden sm:inline">{t("refresh")}</span>
        </Button>
      </header>

      <div
        role="group"
        aria-label={t("filterLabel")}
        className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0"
      >
        <FilterChip active={!filtered} onClick={clearFilters} label={t("filter.all")} />
        {TYPE_FILTERS.map((f) => (
          <FilterChip
            key={f.value}
            active={selected.includes(f.value)}
            onClick={() => toggleType(f.value)}
            label={t(f.labelKey)}
            icon={f.icon}
          />
        ))}
        {(problemCount > 0 || problemsOnly) && (
          <FilterChip
            active={problemsOnly}
            onClick={() => setProblemsOnly((v) => !v)}
            label={t("filter.problems")}
            icon={AlertTriangle}
            count={problemCount}
            danger
          />
        )}
      </div>

      {problemCount > 0 && !problemsOnly && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-destructive">{t("attention.title", { count: problemCount })}</p>
            <p className="text-xs text-muted-foreground">{t("attention.desc")}</p>
          </div>
          <Button size="sm" variant="destructive" onClick={() => setProblemsOnly(true)}>
            {t("attention.show")}
          </Button>
        </div>
      )}

      {query.isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-20 flex-1 rounded-xl" />
            </div>
          ))}
        </div>
      ) : query.isError ? (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3">
          <p className="text-sm text-destructive">{t("error")}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t("retry")}
          </Button>
        </div>
      ) : days.length === 0 ? (
        filtered ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">
              {problemsOnly && selected.length === 0 ? t("noProblems") : t("noFilterMatch")}
            </p>
            <Button variant="outline" size="sm" onClick={clearFilters}>
              {t("clearFilters")}
            </Button>
          </div>
        ) : (
          <EmptyState icon={ActivityIcon} title={t("emptyTitle")} description={t("emptyDesc")} />
        )
      ) : (
        <div className="space-y-2">
          {days.map((day) => (
            <section key={day.key} aria-labelledby={`activity-day-${day.key}`}>
              <h2
                id={`activity-day-${day.key}`}
                className="sticky top-0 z-10 -mx-1 mb-2 bg-background/90 px-1 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur"
              >
                {dayLabel(day.date)}
              </h2>
              <ol>
                {day.items.map((item, i) => (
                  <ActivityTimelineItem key={item.key} item={item} isLast={i === day.items.length - 1} />
                ))}
              </ol>
            </section>
          ))}
        </div>
      )}

      <div ref={sentinel} aria-hidden="true" />
      {query.hasNextPage ? (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>
            {query.isFetchingNextPage ? t("loadingMore") : t("loadMore")}
          </Button>
        </div>
      ) : (
        days.length > 0 &&
        !problemsOnly && <p className="py-4 text-center text-xs text-muted-foreground">{t("caughtUp")}</p>
      )}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  icon: Icon,
  count,
  danger,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon?: LucideIcon;
  count?: number;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active
          ? danger
            ? "border-destructive bg-destructive text-destructive-foreground"
            : "border-foreground bg-foreground text-background"
          : danger
            ? "border-destructive/40 text-destructive hover:bg-destructive/10"
            : "bg-card text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
      {label}
      {count !== undefined && count > 0 && (
        <span
          className={cn(
            "rounded-full px-1.5 text-[10px] leading-4",
            active ? "bg-background/20" : "bg-destructive text-destructive-foreground",
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}
